import { Alert, Form, Input, Row, Select } from "antd"
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { SystemDescriptions } from "src/common/descriptions";
import { User } from "src/common/models/user";
import { UserRole } from "src/common/models/userRole";
import { getInputRequiredRule, stringToBase64 } from "src/common/util";
import { Col6 } from "src/components/Columns";
import { FormActionButtons } from "src/components/FormActionButtons";
import { RootState } from "src/state/reducer";
import { getUserRolesApi } from "../state/action";
import { dictionaryOfRoles } from "../Users/config";
import { UserError, UserErrorOrigin } from "../state/types";

interface Props {
    initialValues?: User
    onFinish: (user: Partial<User>) => void
    onCancel: VoidFunction
}

interface ReduxProps {
    userRoles?: UserRole[]
    error?: UserError
}

export const UserForm = (props: Props) => {
    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(getUserRolesApi())

        if (props.initialValues) {
            form.setFieldsValue({
                name: props.initialValues.name,
                surname: props.initialValues.surname,
                userRole: props.initialValues.userRoleId,
                status: props.initialValues.isActive,
            })
        }
    }, [])

    const reduxProps: ReduxProps = useSelector((state: RootState) => ({
        userRoles: state.user.userRoles || [],
        error: state.user.error,
    }))

    const descriptions = SystemDescriptions.PAGES.USER.CREATE
    const commonDescriptions = SystemDescriptions.PAGES.COMMON

    const [form] = Form.useForm();

    const onFinish = async (formValues) => {
        const user: User = {
            name: formValues.name,
            isActive: formValues.status,
            surname: formValues.surname,
            userRoleId: formValues.userRole,
            password: '',
            username: '',
        }

        if (!props.initialValues) {
            user.password = stringToBase64(formValues.password)
            user.username = formValues.username
        }

        if (props.initialValues) {
            user.id = props.initialValues.id
        }

        props.onFinish(user)
    }

    const getButtonStatus = (): boolean => {
        return props.initialValues
            ? false
            : !form.isFieldsTouched(true) ||
            form.getFieldsError().filter(({ errors }) => errors.length)
                .length > 0
    }

    const handleCancelClick = () => {
        props.onCancel()
    }

    const renderButtons = () => (
        <FormActionButtons
            cancelText={descriptions.CANCEL_BUTTON}
            actionText={descriptions.SAVE_BUTTON}
            actionDisabled={getButtonStatus}
            onCancel={handleCancelClick}
        />
    )

    const renderForm = () => (
        <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            autoComplete={'none'}
            onFinish={onFinish}
        >
            <Row gutter={[16, 8]}>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.NAME.LABEL}
                        name={"name"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.NAME.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.FORM.NAME.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.SURNAME.LABEL}
                        name={"surname"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.SURNAME.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Input
                            placeholder={descriptions.FORM.SURNAME.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col6>
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.ROLE.LABEL}
                        name={"userRole"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.ROLE.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Select
                            options={reduxProps.userRoles?.map(userRole => ({
                                value: userRole.id,
                                label: dictionaryOfRoles[userRole.role],
                            }))}
                            placeholder={descriptions.FORM.ROLE.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col6>
                {
                    !props.initialValues &&
                    <>
                        <Col6>
                            <Form.Item
                                label={descriptions.FORM.USERNAME.LABEL}
                                name={"username"}
                                rules={[
                                    getInputRequiredRule(descriptions.FORM.USERNAME.LABEL),
                                ]}
                                validateTrigger="onBlur"
                            >
                                <Input
                                    placeholder={descriptions.FORM.USERNAME.PLACEHOLDER}
                                />
                            </Form.Item>
                        </Col6>
                        <Col6>
                            <Form.Item
                                label={descriptions.FORM.PASSWORD.LABEL}
                                name={"password"}
                                rules={[
                                    getInputRequiredRule(descriptions.FORM.PASSWORD.LABEL),
                                ]}
                                validateTrigger="onBlur"
                            >
                                <Input
                                    placeholder={descriptions.FORM.PASSWORD.PLACEHOLDER}
                                    type="password"
                                />
                            </Form.Item>
                        </Col6>
                        <Col6>
                            <Form.Item
                                label={descriptions.FORM.PASSWORD_CONFIRM.LABEL}
                                name={"passwordConfirm"}
                                rules={[
                                    getInputRequiredRule(descriptions.FORM.PASSWORD_CONFIRM.LABEL),
                                    {
                                        validator: (_, value) => {
                                            const { password } = form.getFieldsValue()

                                            if (password !== value) {
                                                return Promise.reject(descriptions.ERROR_PASSWORDS_MISSMATCH)
                                            }

                                            return Promise.resolve()
                                        }
                                    },
                                ]}
                                validateTrigger={["onBlur", "onChange"]}
                            >
                                <Input
                                    placeholder={descriptions.FORM.PASSWORD_CONFIRM.PLACEHOLDER}
                                    type="password"
                                />
                            </Form.Item>
                        </Col6>
                    </>
                }
                <Col6>
                    <Form.Item
                        label={descriptions.FORM.STATUS.LABEL}
                        name={"status"}
                        rules={[
                            getInputRequiredRule(descriptions.FORM.STATUS.LABEL),
                        ]}
                        validateTrigger="onBlur"
                    >
                        <Select
                            options={[
                                {
                                    value: 1,
                                    label: commonDescriptions.ACTIVE_STATUS,
                                },
                                {
                                    value: 0,
                                    label: commonDescriptions.INACTIVE_STATUS,
                                },
                            ]}
                            placeholder={descriptions.FORM.STATUS.PLACEHOLDER}
                        />
                    </Form.Item>
                </Col6>
            </Row>
            {
                reduxProps.error && reduxProps.error.type === UserErrorOrigin.UPSERT_USER &&
                <Alert
                    message={"Error"}
                    showIcon
                    description={reduxProps.error?.detail.message ?? commonDescriptions.FORM.UNKNOWN_ERROR}
                    type="error"
                    style={{
                        marginBottom: 12,
                    }}
                />
            }
            {renderButtons()}
        </Form>
    )

    return (
        renderForm()
    )
}
